import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../config/api";
import { checkTokenExpiration } from "../utils/auth";

const AuthContext = createContext();

// Helper function to safely store data
const safeStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error("Failed to store in localStorage:", error);
    return false;
  }
};

// Helper function to safely get data
const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error("Failed to get from localStorage:", error);
    return null;
  }
};

// Helper function to safely remove data
const safeStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn("Failed to remove from storage:", error);
  }
};

// Function to validate and refresh token if needed
const validateAndRefreshToken = async () => {
  try {
    // Check if we have a refresh token
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return false;
    }

    // Try to refresh the token
    const response = await api.post("/api/auth/refresh", {
      refreshToken: refreshToken,
    });

    if (response.data.accessToken) {
      // Store new tokens
      localStorage.setItem("accessToken", response.data.accessToken);
      if (response.data.refreshToken) {
        localStorage.setItem("refreshToken", response.data.refreshToken);
      }
      return true;
    }
    return false;
  } catch (error) {
    // Clear tokens if refresh fails
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Function to check if token needs refresh
  const shouldRefreshToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return false;

    try {
      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;

      // Refresh token if it expires in less than 5 minutes
      return timeUntilExpiry < 300;
    } catch (error) {
      return false;
    }
  };

  // Function to periodically check and refresh tokens
  const startTokenRefreshTimer = () => {
    const checkAndRefresh = async () => {
      if (isLoggedIn && shouldRefreshToken()) {
        try {
          await validateAndRefreshToken();
        } catch (error) {
          console.error("Failed to refresh token:", error);
          logout();
        }
      }
    };

    // Check every 2 minutes
    const interval = setInterval(checkAndRefresh, 2 * 60 * 1000);

    // Also check immediately
    checkAndRefresh();

    return interval;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const userData = safeStorageGet("user");
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");

      // Check token expiration first
      const isTokenValid = checkTokenExpiration();
      if (!isTokenValid) {
        setUser(null);
        setIsLoggedIn(false);
        setIsLoading(false);
        return;
      }

      // If we have user data, use it
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          setUser(parsed);
          setIsLoggedIn(true);
        } catch (error) {
          console.error("Failed to parse user data:", error);
          setUser(null);
          setIsLoggedIn(false);
          safeStorageRemove("user");
        }
      }
      // If we have tokens but no user data, try to refresh and get user info
      else if (accessToken || refreshToken) {
        try {
          const isValid = await validateAndRefreshToken();
          if (isValid) {
            // If tokens are valid but no user data, create a minimal user object
            // The user data will be restored when they interact with the app
            const minimalUser = {
              id: "user",
              email: "user@example.com", // This will be updated when user data is available
              name: "User",
            };
            setUser(minimalUser);
            setIsLoggedIn(true);
            safeStorageSet("user", JSON.stringify(minimalUser));
          } else {
            setUser(null);
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error("Failed to validate tokens:", error);
          setUser(null);
          setIsLoggedIn(false);
        }
      }
      // No tokens or user data
      else {
        setUser(null);
        setIsLoggedIn(false);
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Start token refresh timer when user is logged in
  useEffect(() => {
    let interval;
    if (isLoggedIn) {
      interval = startTokenRefreshTimer();
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLoggedIn]);

  const login = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
    const success = safeStorageSet("user", JSON.stringify(userData));
    if (!success) {
      console.error("Failed to store user data");
    }
  };

  const logout = () => {
    setUser(null);
    setIsLoggedIn(false);
    safeStorageRemove("user");

    // Clear all tokens
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  };

  return (
    <AuthContext.Provider
      value={{ user, isLoggedIn, login, logout, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
